// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import Svg, { Circle, Ellipse, G, Line, Path, Rect, Text as SvgText } from 'react-native-svg';

import appColors from '@/theme/colors';

/**
 * The diagrams for the analysis walkthrough — one per step.
 *
 * Schematic on purpose: each one is drawn to make a single decision legible (why the imagery
 * is windowed, what promotion drops, where a model run stops), not to depict real geometry.
 * They carry no text of their own beyond a handful of short labels, so nothing here needs
 * translating with the prose around it.
 */

const W = 300;
const H = 150;

const ink = appColors.text.primary;
const muted = appColors.text.muted;
const accent = appColors.accent.primary;
const warn = appColors.status.warning;
const bad = appColors.status.danger;

const label = (x: number, y: number, text: string, fill: string = muted, size = 9) => (
  <SvgText x={x} y={y} fill={fill} fontSize={size} textAnchor="middle">
    {text}
  </SvgText>
);

/** The course, covered by overlapping windows — why one mosaic is not enough. */
export function WindowGridDiagram() {
  const cells = [0, 1, 2].flatMap((row) => [0, 1, 2].map((col) => ({ row, col })));
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <Rect x={20} y={16} width={260} height={110} rx={6} fill="none" stroke={muted} strokeDasharray="4 3" />
      {cells.map(({ row, col }) => (
        <Rect
          key={`${row}-${col}`}
          x={26 + col * 84}
          y={22 + row * 34}
          width={90}
          height={40}
          rx={3}
          fill={row === 1 && col === 1 ? accent : 'none'}
          fillOpacity={row === 1 && col === 1 ? 0.18 : 0}
          stroke={row === 1 && col === 1 ? accent : muted}
          strokeOpacity={row === 1 && col === 1 ? 1 : 0.5}
        />
      ))}
      {label(150, 140, 'one window at a time · overlapping so nothing falls between')}
    </Svg>
  );
}

/** Imagery to mask to shapes: what the detector actually does inside one window. */
export function DetectDiagram() {
  const panel = (x: number, title: string, body: React.ReactNode) => (
    <>
      <Rect x={x} y={26} width={80} height={64} rx={4} fill="none" stroke={muted} strokeOpacity={0.6} />
      {body}
      {label(x + 40, 104, title)}
    </>
  );

  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      {panel(
        14,
        'imagery',
        <>
          <Rect x={18} y={30} width={72} height={56} fill={accent} fillOpacity={0.14} />
          <Circle cx={44} cy={54} r={9} fill={warn} fillOpacity={0.5} />
          <Circle cx={70} cy={70} r={6} fill={warn} fillOpacity={0.4} />
        </>
      )}
      {panel(
        110,
        'mask',
        <>
          <Circle cx={140} cy={54} r={9} fill={ink} fillOpacity={0.75} />
          <Circle cx={166} cy={70} r={6} fill={ink} fillOpacity={0.75} />
        </>
      )}
      {panel(
        206,
        'shapes',
        <>
          <Path d="M228 46 L245 48 L248 62 L231 61 Z" fill="none" stroke={accent} strokeWidth={1.5} />
          <Path d="M256 66 L266 67 L267 76 L257 75 Z" fill="none" stroke={accent} strokeWidth={1.5} />
        </>
      )}
      <Line x1={96} y1={58} x2={108} y2={58} stroke={muted} strokeWidth={1} />
      <Line x1={192} y1={58} x2={204} y2={58} stroke={muted} strokeWidth={1} />
      {label(150, 128, 'thresholds, or a trained model — same seam, same downstream')}
    </Svg>
  );
}

/** The funnel: most of what the detector sees never becomes a feature. */
export function FilterFunnelDiagram() {
  const stages: { label: string; width: number; tone: string }[] = [
    { label: 'found', width: 240, tone: muted },
    { label: 'shape gates', width: 180, tone: muted },
    { label: 'near play', width: 130, tone: accent },
    { label: 'attributed to a hole', width: 90, tone: accent },
  ];
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      {stages.map((stage, index) => (
        <Rect
          key={stage.label}
          x={(W - stage.width) / 2}
          y={16 + index * 27}
          width={stage.width}
          height={20}
          rx={3}
          fill={stage.tone}
          fillOpacity={0.18}
          stroke={stage.tone}
          strokeOpacity={0.7}
        />
      ))}
      {stages.map((stage, index) => label(150, 30 + index * 27, stage.label, ink))}
      {label(150, 140, 'each gate drops shapes the ground cannot support')}
    </Svg>
  );
}

/** Two windows saw the same bunker; merge keeps one. */
export function MergeDiagram() {
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <Rect x={40} y={30} width={110} height={70} rx={4} fill="none" stroke={muted} strokeOpacity={0.6} />
      <Rect x={120} y={30} width={110} height={70} rx={4} fill="none" stroke={muted} strokeOpacity={0.6} />
      <Circle cx={135} cy={62} r={11} fill={warn} fillOpacity={0.35} stroke={warn} />
      <Circle cx={135} cy={62} r={11} fill="none" stroke={warn} strokeDasharray="3 2" />
      <Path d="M244 62 L266 62" stroke={muted} strokeWidth={1} />
      <Circle cx={276} cy={62} r={11} fill={accent} fillOpacity={0.35} stroke={accent} />
      {label(135, 118, 'seen twice in the overlap')}
      {label(276, 118, 'kept once')}
    </Svg>
  );
}

/** Promotion is winner-takes-the-ground, and the winner is decided by source. */
export function PrecedenceDiagram() {
  const rows = [
    { name: 'manual', strong: true },
    { name: 'gps', strong: true },
    { name: 'osm', strong: true },
    { name: 'satellite', strong: false },
  ];
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      {rows.map((row, index) => (
        <Rect
          key={row.name}
          x={60}
          y={14 + index * 26}
          width={180 - index * 18}
          height={19}
          rx={3}
          fill={row.strong ? accent : warn}
          fillOpacity={0.2}
          stroke={row.strong ? accent : warn}
          strokeOpacity={0.8}
        />
      ))}
      {rows.map((row, index) => label(150 - index * 9, 27 + index * 26, row.name, ink))}
      {label(150, 138, 'a stronger source already on that ground wins')}
    </Svg>
  );
}

/** A model run stops one step short, on purpose. */
export function ModelStopsDiagram() {
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <Rect x={16} y={34} width={70} height={30} rx={4} fill={accent} fillOpacity={0.18} stroke={accent} />
      {label(51, 53, 'classical', ink)}
      <Path d="M90 49 L112 49" stroke={muted} strokeWidth={1} />
      <Rect x={116} y={34} width={78} height={30} rx={4} fill={accent} fillOpacity={0.18} stroke={accent} />
      {label(155, 53, 'promoted', ink)}
      <Path d="M198 49 L220 49" stroke={muted} strokeWidth={1} />
      <Rect x={224} y={34} width={62} height={30} rx={4} fill={accent} fillOpacity={0.3} stroke={accent} />
      {label(255, 53, 'published', ink)}

      <Rect x={16} y={92} width={70} height={30} rx={4} fill={warn} fillOpacity={0.18} stroke={warn} />
      {label(51, 111, 'model', ink)}
      <Path d="M90 107 L112 107" stroke={muted} strokeWidth={1} />
      <Rect x={116} y={92} width={78} height={30} rx={4} fill={warn} fillOpacity={0.18} stroke={warn} />
      {label(155, 111, 'candidates', ink)}
      <Path d="M198 107 L214 107" stroke={bad} strokeWidth={1} strokeDasharray="3 2" />
      <Path d="M218 101 L230 113 M230 101 L218 113" stroke={bad} strokeWidth={1.5} />
      {label(150, 18, 'two detectors, one of them publishes')}
    </Svg>
  );
}

/** Where the time goes — the same stage names the run's steps panel shows. */
export function StageTimeDiagram() {
  const stages = [
    { name: 'imagery', seconds: 92 },
    { name: 'detect', seconds: 48 },
    { name: 'merge', seconds: 2 },
    { name: 'promote', seconds: 1 },
  ];
  const max = Math.max(...stages.map((s) => s.seconds));
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      {stages.map((stage, index) => (
        <Rect
          key={stage.name}
          x={72}
          y={18 + index * 26}
          width={Math.max(4, (stage.seconds / max) * 190)}
          height={16}
          rx={3}
          fill={index === 0 ? warn : accent}
          fillOpacity={0.35}
          stroke={index === 0 ? warn : accent}
          strokeOpacity={0.8}
        />
      ))}
      {stages.map((stage, index) => (
        <SvgText key={stage.name} x={64} y={31 + index * 26} fill={ink} fontSize={9} textAnchor="end">
          {stage.name}
        </SvgText>
      ))}
      {label(150, 138, 'fetching pixels is most of a run — reuse of the stored basemap is the lever')}
    </Svg>
  );
}

/** One request, many jobs: the round fans out and the map is decided again as answers land. */
export function JobFanOutDiagram() {
  const lanes = [
    { name: 'LM · classical', x0: 96, x1: 222, tone: accent, decided: true },
    { name: 'Esri · classical', x0: 96, x1: 162, tone: accent, decided: true },
    { name: 'LM · model', x0: 96, x1: 238, tone: warn, decided: false },
    { name: 'canopy', x0: 122, x1: 178, tone: accent, decided: true },
    { name: 'elevation', x0: 96, x1: 132, tone: muted, decided: false },
  ];
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <Rect x={12} y={54} width={56} height={30} rx={4} fill={accent} fillOpacity={0.18} stroke={accent} />
      {label(40, 73, 'request', ink)}
      {lanes.map((lane, index) => {
        const y = 12 + index * 24;
        return (
          <G key={lane.name}>
            <Path d={`M68 69 C 82 69, 82 ${y + 9}, 96 ${y + 9}`} stroke={muted} strokeOpacity={0.6} fill="none" />
            <Rect
              x={lane.x0}
              y={y + 2}
              width={lane.x1 - lane.x0}
              height={14}
              rx={3}
              fill={lane.tone}
              fillOpacity={0.3}
              stroke={lane.tone}
            />
            <SvgText x={lane.x0 + 4} y={y + 12} fontSize={8} fill={ink}>
              {lane.name}
            </SvgText>
            {lane.decided ? <Path d={`M${lane.x1} ${y + 3} l5 6 l-5 6 l-5 -6 z`} fill={accent} /> : null}
          </G>
        );
      })}
      <Rect x={252} y={54} width={40} height={30} rx={4} fill={accent} fillOpacity={0.3} stroke={accent} />
      {label(272, 73, 'map', ink)}
      {label(150, 142, 'jobs run side by side · \u25c6 the map is decided again after each publishing answer')}
    </Svg>
  );
}

/** The measured canopy as a second opinion on a tree blob: covered moves it up, bare moves it down. */
export function CanopyNudgeDiagram() {
  return (
    <Svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`}>
      <Rect x={26} y={34} width={118} height={78} rx={10} fill={accent} fillOpacity={0.14} stroke={accent} strokeDasharray="4 3" />
      {label(85, 28, 'laser canopy \u2265 2 m', accent)}
      <Ellipse cx={85} cy={72} rx={34} ry={22} fill={warn} fillOpacity={0.35} stroke={warn} />
      {label(85, 75, 'trees?', ink)}
      {label(85, 126, 'covered \u2192 +0.10', ink)}
      <Ellipse cx={218} cy={72} rx={34} ry={22} fill={warn} fillOpacity={0.35} stroke={warn} />
      {label(218, 75, 'trees?', ink)}
      {label(218, 126, 'nothing tall \u2192 \u22120.10', ink)}
      {label(150, 144, 'height from above does not care about shadow')}
    </Svg>
  );
}
